import { ActionButton, ActionButtonType } from "@octopusdeploy/design-system-components";
import { StringHelper } from "@octopusdeploy/design-system-octopus-components";
import { MachineModelHealthStatusValues, type MachineModelHealthStatus, type WorkerMachineResource, type WorkerPoolResource } from "@octopusdeploy/octopus-server-client";
import { links } from "@octopusdeploy/portal-routes";
import * as React from "react";
import { AdvancedWorkerConfigurationPage } from "~/areas/infrastructure/components/MachineSettings/Endpoints/KubernetesAgent/AdvancedWorkerConfigurationPage";
import { BasicWorkerConfigurationPage } from "~/areas/infrastructure/components/MachineSettings/Endpoints/KubernetesAgent/BasicWorkerConfigurationPage";
import type { ConfigurationProps, ConfigurationState } from "~/areas/infrastructure/components/MachineSettings/Endpoints/KubernetesAgent/ConfigurationDialog";
import { ConfigurationDialog } from "~/areas/infrastructure/components/MachineSettings/Endpoints/KubernetesAgent/ConfigurationDialog";
import { NfsDriverInstallationHelpContent, TentacleInstallationHelpContent, tentacleInstallationHelpImage, WorkerConfigurationHelpContent, workerConfigurationHelpImage, workerNfsDriverInstallationHelpImage, } from "~/areas/infrastructure/components/MachineSettings/Endpoints/KubernetesAgent/ConfigurationDialogHelp";
import { ConfigurationMessages } from "~/areas/infrastructure/components/MachineSettings/Endpoints/KubernetesAgent/ConfigurationMessages";
import { InstallationPage } from "~/areas/infrastructure/components/MachineSettings/Endpoints/KubernetesAgent/InstallationPage";
import type { BaseConfiguration } from "~/areas/infrastructure/components/MachineSettings/Endpoints/KubernetesAgent/configurationUtils";
import { createWorkerHelmCommand } from "~/areas/infrastructure/components/MachineSettings/Endpoints/KubernetesAgent/createHelmCommand";
import { EndpointSelectionScope } from "~/areas/infrastructure/components/MachineSettings/Endpoints/endpointRegistry";
import { repository } from "~/clientInstance";
import type { PagedOnboardingDialogPageData, PageNavigation } from "~/components/OnboardingDialog/PagedOnboardingDialog";
type WorkerFieldErrors = {
    [K in keyof WorkerConfigurationResource]?: string;
};
interface WorkerConfigurationState extends ConfigurationState<WorkerConfigurationResource> {
    workerPools: WorkerPoolResource[];
    foundWorkerResource?: WorkerMachineResource;
}
export interface WorkerConfigurationResource extends BaseConfiguration {
    WorkerPools: string[];
}
export class KubernetesAgentWorkerConfigurationDialog extends ConfigurationDialog<WorkerConfigurationState, WorkerConfigurationResource, ConfigurationProps<WorkerConfigurationResource>> {
    constructor(props: ConfigurationProps<WorkerConfigurationResource>) {
        super(props, EndpointSelectionScope.Worker, ConfigurationMessages.Name.Worker);
    }
    onWorkerFound = (worker: WorkerMachineResource) => {
        this.setState({ foundWorkerResource: worker });
    };
    onWorkerHealthStatusUpdated = (worker: WorkerMachineResource) => {
        this.setState({ foundWorkerResource: worker });
    };
    override initialModel() {
        return {
            ...this.getInitialBaseModel(),
            WorkerPools: [],
        };
    }
    override initialiseState() {
        this.state = {
            ...this.initialBaseState,
            workerPools: [],
        };
    }
    override async loadMachineTypeSpecificData() {
        const workerPoolsPromise = repository.WorkerPools.all();
        this.setState({ workerPools: await workerPoolsPromise });
    }
    override async validateMachineTypeSpecificData(model: WorkerConfigurationResource, fieldErrors: WorkerFieldErrors): Promise<void> {
        if (StringHelper.isNullOrWhiteSpace(model.Name)) {
            fieldErrors.Name = this.nameErrorMessages.Validation.NotProvided;
        }
        else {
            const existingWorker = await repository.Workers.list({ name: this.state.model.Name });
            if (existingWorker.TotalResults > 0) {
                fieldErrors.Name = this.nameErrorMessages.Validation.AlreadyExists;
            }
        }
        if (model.WorkerPools.length === 0) {
            fieldErrors.WorkerPools = ConfigurationMessages.WorkerPools.Validation.NotProvided;
        }
    }
    override machineIsFound() {
        return !!this.state.foundWorkerResource;
    }
    override machineHealthStatus(): MachineModelHealthStatus | undefined {
        return this.state.foundWorkerResource?.HealthStatus;
    }
    override getLinkToMachine() {
        if (this.state.foundWorkerResource) {
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            return links.workerSettingsPage.generateUrl({ spaceId: repository.spaceId!, machineId: this.state.foundWorkerResource.Id });
        }
        return undefined;
    }
    async refreshWorkerPools(newWorkerPool?: WorkerPoolResource): Promise<void> {
        await this.loadMachineTypeSpecificData();
        if (newWorkerPool) {
            this.setModelState({ WorkerPools: [...this.state.model.WorkerPools, newWorkerPool.Id] });
        }
    }
    validateBasicFields = async (): Promise<boolean> => {
        this.clearErrors();
        const fieldErrors: WorkerFieldErrors = {};
        const model = this.state.model;
        if (StringHelper.isNullOrWhiteSpace(model.Name)) {
            fieldErrors.Name = this.nameErrorMessages.Validation.NotProvided;
        }
        else {
            const existingWorker = await repository.Workers.list({ name: this.state.model.Name });
            if (existingWorker.TotalResults > 0) {
                fieldErrors.Name = this.nameErrorMessages.Validation.AlreadyExists;
            }
        }
        if (model.WorkerPools.length === 0) {
            fieldErrors.WorkerPools = ConfigurationMessages.WorkerPools.Validation.NotProvided;
        }
        if (Object.keys(fieldErrors).length > 0) {
            this.setValidationErrors("The following fields have invalid values", fieldErrors);
            return false;
        }
        return true;
    };
    override getFirstConditionalPageIndex(): number {
        return 2;
    }
    override getPages() {
        const showRegenerateTokenButton = this.state.hasTokenExpired && !this.state.foundWorkerResource;
        let pageIndex = 1; // Set to 1 because we aren't incrementing until after the first page is added
        const pages: PagedOnboardingDialogPageData[] = [
            {
                name: "Add Kubernetes Worker",
                title: "Add New Kubernetes Worker",
                progressTrackerLabel: "Configure",
                helpPanelContent: <WorkerConfigurationHelpContent />,
                helpPanelImage: workerConfigurationHelpImage(),
                isPageModelValid: () => this.validateBasicFields(),
                hideBackButton: true,
                alwaysShowCancelButton: true,
                isBusy: this.state.isBusy,
                customPrimaryButton: (pageNavigation: PageNavigation) => (<ActionButton label="Quick Setup" type={ActionButtonType.Primary} onClick={async () => {
                        if (await this.validateBasicFields()) {
                            if (!this.state.requiresHaConfiguration) {
                                await this.refreshToken();
                            }
                            pageNavigation.moveToPageIndex(this.getFirstConditionalPageIndex());
                        }
                    }}/>),
                customSecondaryButton: (pageNavigation: PageNavigation) => (<ActionButton label="Advanced Setup" onClick={async () => {
                        if (await this.validateBasicFields()) {
                            if (!this.state.requiresHaConfiguration) {
                                await this.refreshToken();
                            }
                            this.setState({ advancedVisited: true });
                            await pageNavigation.moveToNextPage();
                        }
                    }} disabled={!pageNavigation.canMoveToNext}/>),
                render: () => (<BasicWorkerConfigurationPage model={this.state.model} onModelChange={(model) => this.setModelState(model)} workerPools={this.state.workerPools} getFieldError={(name: string) => this.getFieldError(name)} refreshWorkerPools={(newWorkerPool?: WorkerPoolResource) => this.refreshWorkerPools(newWorkerPool)}/>),
            },
            {
                name: "Advanced Settings",
                title: "Advanced Settings",
                progressTrackerLabel: this.state.advancedVisited ? "Advanced" : undefined,
                helpPanelContent: <WorkerConfigurationHelpContent />,
                helpPanelImage: workerConfigurationHelpImage(),
                isPageModelValid: () => this.validateAdvancedFields(),
                isBusy: this.state.isBusy,
                customSecondaryButton: this.moveToBasicOrAdvancedPageBackButton(pageIndex++),
                hideBackButton: true,
                render: () => (<AdvancedWorkerConfigurationPage model={this.state.model} onModelChange={(model) => this.setModelState(model)} canModifyServerUrls={this.state.canModifyServerUrls} getFieldError={(name: string) => this.getFieldError(name)} usePersistentVolumeStorage={this.state.usePersistentVolumeStorage} onUsePersistentVolumeStorageChanged={(usePv) => this.setState({ usePersistentVolumeStorage: usePv })} requiresHaConfiguration={this.state.requiresHaConfiguration}/>),
            },
        ];
        if (this.state.requiresHaConfiguration) {
            pages.push(this.getHaConfigurationPage(<WorkerConfigurationHelpContent />, workerConfigurationHelpImage(), this.moveToBasicOrAdvancedPageBackButton(pageIndex++)));
        }
        if (!this.state.usePersistentVolumeStorage) {
            pages.push(this.getNfsCsiDriverInstallationPage(<NfsDriverInstallationHelpContent machineType={EndpointSelectionScope.Worker}/>, workerNfsDriverInstallationHelpImage(), this.moveToBasicOrAdvancedPageBackButton(pageIndex++)));
        }
        const installationComplete = this.props.guidedSetup ? this.machineHealthStatus() === MachineModelHealthStatusValues.Healthy : undefined;
        pages.push({
            name: "Install Kubernetes Worker",
            title: "Install Kubernetes Worker",
            progressTrackerLabel: "Install",
            helpPanelContent: <TentacleInstallationHelpContent machineType={EndpointSelectionScope.Worker}/>,
            helpPanelImage: tentacleInstallationHelpImage(),
            render: () => (<InstallationPage<WorkerMachineResource> model={this.state.model} onAgentFound={this.onWorkerFound} onAgentHealthStatusUpdated={this.onWorkerHealthStatusUpdated} hasTokenExpired={this.state.hasTokenExpired} accessTokenExpiry={this.state.accessTokenExpiry} generateHelmCommand={(selectedLanguage) => createWorkerHelmCommand(selectedLanguage, this.state.model, this.state.workerPools)} fetchAgent={async (repository, name: string) => {
                    const workers = await repository.Workers.list({ partialName: name });
                    return workers.Items.find((t) => t.Name === name);
                }} machineType={EndpointSelectionScope.Worker}/>),
            alwaysShowCloseButton: this.props.guidedSetup ? true : this.machineIsFound(),
            onlyShowCloseButton: installationComplete,
            customSecondaryButton: !installationComplete ? this.moveToBasicOrAdvancedPageBackButton(pageIndex++) : undefined,
            hideBackButton: true,
            customPrimaryButton: (showRegenerateTokenButton && <ActionButton label="Regenerate token" onClick={() => this.refreshToken()} type={ActionButtonType.Primary}/>) || undefined,
            onPageDisplayed: async () => {
                this.props.onEstablishingConnection?.(this.state.model);
            },
        });
        return pages;
    }
}
